import * as React from 'react';
import { IServerUserInfo } from '../../ts/models/IServerUserInfo';
import { ServiceLocator } from '../../app/ServiceLocator';
import { Set } from '../../ts/models/Set';
import { ButtonWidget, ButtonType } from '@tableau/widgets';
import { UserSettingsStyle } from '../styles/UserSettingsStyle';
import { Messages } from '../../codegen/Localize';
import _ = require('underscore');
import { IOAuthCredential, IOAuthCredentialTypeInfo } from '../../ts/ServerApi/types/IOAuthCredential';
import { SliceableHelper } from '../../ts/helpers/SliceableHelper';
import { OAuthCredentials } from '../../connectivity/resources/OAuthCredentials';
import { ListResult } from '../../ts/fetchers/ListResult';
import { OAuthCredentialsService } from '../../connectivity/services/OAuthCredentialsService';
import { CredentialTypeFilter } from '../services/filters/CredentialTypeFilter';
import { AddOAuthCredentialAction } from '../../connectivity/services/AddOAuthCredentialAction';
import { openReactActionDialog } from '../../commonInfra/dialogs/ReactActionDialog';
import { AddNewOAuthSavedCredentialBody, AddNewOAuthSavedCredentialBodyProps, AddNewOAuthSavedCredentialBodyState } from './AddNewOAuthSavedCredentialBody';
import { TabDialogWidth } from '../../commonInfra/dialogs/style/TabDialogWidth';
import { showDeleteOAuthCredentialsDialog } from '../../ts/siteManagement/services/DeleteOAuthCredentialsAction';
import { showDeleteSavedPasswordActionDialog } from '../../ts/siteManagement/services/DeleteMyCredentialsAction';


export interface OAuthCredentialViewModel  {
  credential: IOAuthCredential;
  test: {
    // Determine if test button is displayed to customers, as of Q4 2016, we disable them for
    // generic-auth-based WDC credentials. TFS 546010
    enabled?: boolean;
  };
}

export interface OAuthCredentialsList {
  typeInfo: IOAuthCredentialTypeInfo;
  addEnabled: boolean;
  credentials?: OAuthCredentialViewModel[];
}

export type SavedCredentialsForDataSourceSettingProps = {
  serverUser: IServerUserInfo;
}

const createOAuthCredentialLists = (credentials?: IOAuthCredential[]): OAuthCredentialsList[] => {
  const convertToCredentialViewModel = (credential: IOAuthCredential): OAuthCredentialViewModel => {
    return {credential: credential, test: { enabled: OAuthCredentialsService.supportsTestConnection(credential.type) }};
  };

  const credentialVMGroupsMap = _.groupBy(credentials, c => c.type);

  // Iterate through all OAuth types, if any is enabled, put it in  the result
  return _.compact(_.map(OAuthCredentialsService.getAllOAuthCredentialTypes(),
    (typeInfo: IOAuthCredentialTypeInfo) => {
      const oauthCredentialsList: OAuthCredentialsList = {
        typeInfo: typeInfo,
        addEnabled: OAuthCredentials.canAddForConnectionType(typeInfo),
        credentials: _.map(credentialVMGroupsMap[typeInfo.type], convertToCredentialViewModel)
      };

      return (oauthCredentialsList.addEnabled || oauthCredentialsList.credentials.length > 0) ? oauthCredentialsList : null;
    })
  );
};

export function SavedCredentialsForDataSourceSetting(props: SavedCredentialsForDataSourceSettingProps): JSX.Element {
  const [credentialsLists, setCredentialsLists] = React.useState(createOAuthCredentialLists() as OAuthCredentialsList[]);

  async function getCredentialsLists() {
    const credentialsListResult: ListResult<IOAuthCredential> = await SliceableHelper.fetchAll(OAuthCredentials.all());
    setCredentialsLists(createOAuthCredentialLists(credentialsListResult.items));
  }

  React.useEffect(() => {
    getCredentialsLists();
  }, []);

  const getOauthServerRoleAndRedirect = (typeInfo: IOAuthCredentialTypeInfo) => {
    openReactActionDialog<AddNewOAuthSavedCredentialBodyProps, AddNewOAuthSavedCredentialBodyState, any>({
      title:  Messages.addNewSavedCredentialTitle(),
      bodyComponent: AddNewOAuthSavedCredentialBody,
      initialBodyProps: {
        prompt: Messages.customAuthenticationSettings(),
        supportsOAuthRole: (typeInfo.type === 'snowflake')
      },
      initialDialogState: { server: '', role: '' },
      actionName: Messages.add(),
      dismissDialogOnActionFailed: true,
      action: (_bodyProps: AddNewOAuthSavedCredentialBodyProps, dialogState: AddNewOAuthSavedCredentialBodyState) => {
        const server = dialogState.server;
        var instanceUrl;
        if (server.startsWith('https://')  ||  server.startsWith('http://')){
          instanceUrl = 'https://' + server.split('/')[2];
        }else{
          instanceUrl = 'https://' + server;
        }
        AddOAuthCredentialAction.attemptAuth(typeInfo.type, instanceUrl,
        dialogState.role).then(getCredentialsLists);
        return Promise.resolve();
      },
      actionDisabled: (_bodyProps: AddNewOAuthSavedCredentialBodyProps, dialogState: AddNewOAuthSavedCredentialBodyState) => dialogState.server.length === 0,
      dialogWidth: TabDialogWidth.width500px,
  });
};

  const addCredential = async (typeInfo: IOAuthCredentialTypeInfo) => {
    if (typeInfo.type === 'snowflake' || typeInfo.supportsCustomDomain === true) {
      getOauthServerRoleAndRedirect(typeInfo);
    }
    else {
      await AddOAuthCredentialAction.attemptAuth(typeInfo.type);
      getCredentialsLists();
    }
  };

  const deleteCredential = async (oauthCredential: OAuthCredentialViewModel) => {
    await showDeleteOAuthCredentialsDialog(Set.fromIdItemArray([oauthCredential.credential]));
    getCredentialsLists();
  };

  const deleteAllCredentials = async () => {
    await showDeleteSavedPasswordActionDialog();
    getCredentialsLists();
  };

  return (
    <table>
    <tbody className={UserSettingsStyle.tbodyNew}>
        {/* Saved Credentials Section */}
        <tr>
          <td className={UserSettingsStyle.labelTextNew}>
              {Messages.savedCredentials()}
          </td>
          <td>
            <div style={{paddingLeft: '510px', paddingTop: '10px'}}>
              <ButtonWidget testId= "saved-credentials-delete-all"
                buttonType={ ButtonType.Outline }
                handleClick={ deleteAllCredentials }>
                { Messages.deleteAllCredentialsAndPasswords_action() }
              </ButtonWidget>
            </div>
          </td>
        </tr>
        {credentialsLists.length > 0 &&
          <tr data-test-id="saved-credentials-section">
            <td></td>
            <td className={UserSettingsStyle.detailsValue} colSpan={2}>
              <div className="tb-oauth-table tb-enable-selection" style={{width: '727px'}}>
              {credentialsLists.map(credentialList =>
                <div className="tb-oauth-type" key={credentialList.typeInfo.type}data-tb-test-id={'tb-credentials-' + credentialList.typeInfo.type + '-parent'}>
                  <div className="tb-oauth-type-header">
                    <span className="tb-oauth-type-title" data-tb-test-id={'tb-credential-' + credentialList.typeInfo.type + '-title'}>
                      {CredentialTypeFilter.filter(credentialList.typeInfo)}
                    </span>
                    {credentialList.addEnabled &&
                      <a className="tb-oauth-type-add"
                      data-tb-test-id={'tb-credential-' + credentialList.typeInfo.type + '-add'}
                      href={AddOAuthCredentialAction.href(credentialList.typeInfo)}
                      onClick={(event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {addCredential(credentialList.typeInfo); event.preventDefault();}}>
                        {Messages.add()}
                      </a>}
                  </div>
                  {credentialList.credentials.map(credRow =>
                    <SavedCredentialsTypeRow credRow={credRow} deleteCredential={deleteCredential} key={credRow.credential.id}/>
                  )}
                </div>
              )}
              </div>
            </td>
          </tr>}
    </tbody>
    </table>
  );
}

export type SavedCredentialsTypeRowProps = {
  credRow: OAuthCredentialViewModel;
  deleteCredential: (oauthCredential: OAuthCredentialViewModel) => Promise<void>;
}

export function SavedCredentialsTypeRow(props: SavedCredentialsTypeRowProps): JSX.Element {
  const [isTestInProgress, setIsTestInProgress] = React.useState(false);
  const [isTestSuccessful, setIsTestSuccessful] = React.useState(undefined);
  const credRow = props.credRow;

  const testCredential = (oauthCredential: OAuthCredentialViewModel) => {
    setIsTestInProgress(true);
    const handleTestResult = (success: any) => {
      setIsTestSuccessful(!!success);
      setIsTestInProgress(false);
    };
    OAuthCredentials.test(oauthCredential.credential.id)
      .then(handleTestResult, () => handleTestResult(false));
  };

  return (
    <div className="tb-oauth-type-row">
      <div className="tb-oauth-type-row-details">
        <span className="tb-oauth-type-username" title={credRow.credential.username} data-tb-test-id={'tb-credential-' + credRow.credential.id + '-username'}>
          <span>{credRow.credential.username}<br></br></span>
          {credRow.credential.instanceUrl && <span>{credRow.credential.instanceUrl}<br></br></span>}
          {credRow.credential.role && <span>{credRow.credential.role}<br></br></span>}
        </span>
        {/* eslint-disable jsx-a11y/anchor-is-valid */
        /* eslint-disable jsx-a11y/click-events-have-key-events */
        /* eslint-disable jsx-a11y/no-static-element-interactions */
        <a className="tb-oauth-type-delete"
          data-tb-test-id={'tb-credential-' + credRow.credential.id + '-delete-button'}
          onClick={(event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {props.deleteCredential(credRow); event.preventDefault();}}>
            {Messages.deleteMessage()}
        </a>}
        {credRow.test.enabled &&
        /* eslint-disable jsx-a11y/anchor-is-valid */
        /* eslint-disable jsx-a11y/click-events-have-key-events */
        /* eslint-disable jsx-a11y/no-static-element-interactions */
          <a className="tb-oauth-type-test"
            data-tb-test-id={'tb-credential-' + credRow.credential.id + '-test-button'}
            onClick={(event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {testCredential(credRow); event.preventDefault();}}>
              {Messages.test()}
          </a>}
      </div>
      <div className="tb-oauth-test-results">
        {isTestInProgress && <span className="tb-icon-spinner tb-lr-padded-narrow"></span>}
        {!isTestInProgress && isTestSuccessful === true &&
          <span className="tb-oauth-test-message" data-tb-test-id={'tb-credential-' + credRow.credential.id + '-testing-message'}>
            {Messages.oauthCredentialTestSuccess()}
          </span>}
        {!isTestInProgress && isTestSuccessful === false &&
        <span className="tb-oauth-test-message tb-error" data-tb-test-id={'tb-credential-' + credRow.credential.id + '-error-message'}>
          {Messages.oauthCredentialTestError()}
        </span>}
      </div>
    </div>
  );
}
